import React, { useState, useRef, useContext, useEffect } from 'react';
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import { PriorityHigh, AttachFile, People, Close } from '@mui/icons-material';
import DynamicTextFields from './DynamicTextFields';
import { useDialog } from '../contexts/DialogContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { CustomTextField } from '../styles/SharedStyles';
import { CustomSelectField } from './CustomSelectField';
import { WithdrawalContext } from '../contexts/WithdrawalContext';
import { formatAmount } from '../utils/utils';
import CaptchaDialog from "./CaptchaDialog";
import { BankAccountContext } from '../contexts/BankAccountContext';

const PayoutForm = ({ withdrawal, onBack, onCancelStart, onSubmit }) => {
  const { cancelWithdrawal, loading, submitWithdrawal } = useContext(WithdrawalContext);
  const { openDialog, closeDialog } = useDialog();
  const { showSnackbar } = useSnackbar();
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isAttachmentFocused, setIsAttachmentFocused] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isCaptchaOpen, setIsCaptchaOpen] = useState(false);
  const allowedExtensions = [
    ".docx", ".doc", ".dot", ".pdf",
    ".jpe", ".jpeg", ".jpg", ".gif", ".pjpeg",
    ".bmp", ".png", ".heic", ".heif"
  ];
  const [isTagIdSelectFocused, setIsTagIdSelectFocused] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const hasAutoSelectedAccount = useRef(false);
  const { bankAccounts, getUserBankAccount } = useContext(BankAccountContext);
  const hasBankAccounts = Array.isArray(bankAccounts) && bankAccounts.length > 0;
  const hasError = !loading && !withdrawal;

  const [errors, setErrors] = useState({
    bankAccount: false,
    files: false,
  });
  const validateForm = () => {
    const newErrors = {
      bankAccount: false,
      files: files.length === 0,
    };

    setErrors(newErrors);

    return !newErrors.files;
  };

  useEffect(() => {
    getUserBankAccount();
  }, [getUserBankAccount]);

  useEffect(() => {
    if (!hasBankAccounts) {
      hasAutoSelectedAccount.current = false;
      setSelectedAccount('');
      setErrors((prev) => ({ ...prev, bankAccount: false }));
      return;
    }

    const accountExists = bankAccounts.some((account) => account.bankAccountId === selectedAccount);
    if (selectedAccount && !accountExists) {
      setSelectedAccount(bankAccounts[0].bankAccountId);
      setErrors((prev) => ({ ...prev, bankAccount: false }));
      hasAutoSelectedAccount.current = true;
      return;
    }

    if (!hasAutoSelectedAccount.current && !selectedAccount) {
      setSelectedAccount(bankAccounts[0].bankAccountId);
      hasAutoSelectedAccount.current = true;
    }
  }, [bankAccounts, hasBankAccounts, selectedAccount]);

  const handleAccountChange = (event) => {
    setSelectedAccount(event.target.value);
    setErrors((prev) => ({ ...prev, bankAccount: false }));
  };

  const clearFilesAndRemount = () => {
    setFiles([]);
    try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (e) { }
  };

  useEffect(() => {
    clearFilesAndRemount();
    const onPageShow = () => clearFilesAndRemount();
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);


  const getUniqueFileName = (name, existingFiles) => {
    const names = existingFiles.map(f => f.name);
    if (!names.includes(name)) return name;

    const dotIndex = name.lastIndexOf('.');
    const base = dotIndex !== -1 ? name.substring(0, dotIndex) : name;
    const ext = dotIndex !== -1 ? name.substring(dotIndex) : '';
    let counter = 1;
    let newName = `${base} (${counter})${ext}`;
    while (names.includes(newName)) {
      counter++;
      newName = `${base} (${counter})${ext}`;
    }
    return newName;
  };

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;

    const validFiles = [];

    selected.forEach(file => {
      const extension = "." + file.name.split(".").pop().toLowerCase();

      if (!allowedExtensions.includes(extension)) {
        showSnackbar(`不支持的文件格式: ${file.name}`, "error");
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length === 0) {
      try { event.target.value = ''; } catch (e) { }
      try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (e) { }
      return;
    }

    setFiles(prev => {
      const newFiles = [...prev];

      validFiles.forEach(file => {
        const uniqueName = getUniqueFileName(file.name, newFiles);
        const renamedFile = new File([file], uniqueName, { type: file.type, lastModified: file.lastModified });
        newFiles.push(renamedFile);
      });

      return newFiles;
    });

    try { event.target.value = ''; } catch (e) { }
    try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (e) { }

    setErrors((prev) => ({ ...prev, files: false }));
  };

  const handleRemoveFile = (event) => {
    event.stopPropagation();
    event.preventDefault();
    clearFilesAndRemount();
  };

  const handleConfirmTransfer = () => {
    openDialog(
      {
        title: '申购抢单',
        message: '确定完成转账吗？',
        onConfirm: () => {
          setIsConfirm(true);
          closeDialog();
        }
      });
  };

  const handleCancel = async () => {
    onCancelStart?.();

    const result = await cancelWithdrawal(withdrawal.id);
    if (result.isSuccess) {
      showSnackbar('撤销成功!', 'info', null, false);
    }
    await onBack();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const response = await submitWithdrawal(withdrawal.id, selectedAccount || 0, files);

    if (response.isSuccess) {
      showSnackbar('上传成功!', 'info', null, false);
      onSubmit();
    }
    else {
      onBack();
    }
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Open the file upload dialog
    }
  };

  if (loading) {
    // Show a loading indicator while fetching data
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          flex: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (hasError) {
    // Show an error message if the withdrawal is not found
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography color="error">无法加载订单详情</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'var(--bg-secondary-color)',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        boxSizing: 'border-box',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          width: '100%',
          boxSizing: 'border-box',
          padding: '20px 16px 28px',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'var(--primary-color)',
            padding: '0 16px',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          申购转账
        </Typography>

        {/* Warning Notice*/}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'start',
            border: '1px solid #f57c00', // Border color
            borderRadius: '8px',
            padding: 2,
            backgroundColor: '#121212', // Dark background
            color: '#f57c00', // Text color
            margin: '16px 0',
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <PriorityHigh
            sx={{
              fontSize: '1.5rem',
              marginRight: '12px',
            }}
          />

          <Typography
            sx={{
              fontSize: '0.875rem', // Small font size
              lineHeight: '1.5',
              wordBreak: 'break-word',
            }}
          >
            请准确按照汇款金额 {formatAmount(withdrawal.amount)} 转账
            <br />
            请使用绑定在我方平台且验证成功的银行卡支付此单，以免造成不必要的资金损失。
            如需使用未在我方验证成功的银行卡付款，请联系客服处理。
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            mt: 3
          }}
        >
          {/* QR Code */}
          {withdrawal?.qrCode && (
            <Box
              component="img"
              src={withdrawal.qrCode}
              alt="QR Code"
              sx={{
                width: { xs: '100%', md: '40%' },
                maxWidth: '100%',
                height: '240px',
                objectFit: 'contain',
                mb: 2,
              }}
            />
          )}

          {/* Text Fields Section */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 0,
            }}
          >
            <DynamicTextFields
              fields={[
                { label: '订单号', value: withdrawal.orderId },
                { label: '收款银行', value: withdrawal.customerBankName },
                { label: '收款账号', value: withdrawal.customerBankAccountNumber },
                { label: '收款姓名', value: withdrawal.customerBankAccountName },
                { label: '汇款金额', value: formatAmount(withdrawal.amount, 'en-US', null, false) },
              ]}
              showCopyButton={true}
            />
          </Box>
        </Box>

        {
          isConfirm
            ? <Box sx={{ width: '100%' }}>
              {hasBankAccounts && (
                <Box
                  sx={{
                    marginBottom: 2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                    <People
                      sx={{
                        color: isTagIdSelectFocused ? 'var(--primary-color)' : 'var(--secondary-color)',
                        marginRight: 1,
                        padding: 0,
                      }}
                    />
                    <CustomSelectField
                      id="withdrawal-account-select"
                      label="出款账号"
                      value={selectedAccount}
                      onChange={handleAccountChange}
                      onFocus={() => setIsTagIdSelectFocused(true)}
                      onBlur={() => setIsTagIdSelectFocused(false)}
                      options={bankAccounts}
                      optionValueKey="bankAccountId"
                      optionLabelKey="tagId"
                      includeEmptyOption={false}
                    />
                  </Box>
                  {errors.bankAccount && (
                    <Typography variant="caption" sx={{ color: 'var(--error-color)', mt: 1, textAlign: 'right' }}>
                      出款账号为必填项
                    </Typography>
                  )}
                </Box>
              )}
              <Box
                sx={{
                  marginBottom: 3,
                  width: '100%',
                }}
              >
                <Box
                  id="withdrawal-attachment"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  {/* Attach File Icon */}
                  <AttachFile
                    sx={{
                      color: isAttachmentFocused ? 'var(--primary-color)' : 'var(--secondary-color)',
                      marginRight: 1,
                      padding: 0,
                    }}
                  />

                  {/* TextField */}
                  <label style={{ width: '100%', position: 'relative' }}>
                    <CustomTextField
                      fullWidth
                      variant="outlined"
                      label="点击选择带公章回执单 (必填)"
                      value={files.length === 0 ? '' : `${files.length}个文件`}
                      onClick={triggerFileInput} // Open the upload dialog when focused
                      onFocus={() => setIsAttachmentFocused(true)} // Set focus state to true
                      onBlur={() => setIsAttachmentFocused(false)} // Reset focus state to false
                      slotProps={{
                        input: {
                          readOnly: true,
                          endAdornment: files.length > 0 && (
                            <IconButton
                              onClick={(event) => handleRemoveFile(event)} // Remove the first file as an example
                              onMouseDown={(e) => e.preventDefault()} // stops focus, prevents file dialog
                              sx={{ color: 'var(--primary-color)', position: 'relative', zIndex: 3 }}
                            >
                              <Close />
                            </IconButton>
                          ),
                        }
                      }}
                    />

                    {/* Hidden File Input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx,.doc,.dot,.pdf,.jpe,.jpeg,.jpg,.gif,.pjpeg,.bmp,.png,.heic,.heif"
                      multiple
                      onChange={handleFileChange}
                      style={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0,
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        zIndex: 1,
                      }}
                      id="file-upload-attachment"
                    />
                  </label>
                </Box>
                {errors.files && (
                  <Typography variant="caption" sx={{ color: 'var(--error-color)', mt: 1, textAlign: 'right' }}>
                    附件是必填项，请上传至少一个文件
                  </Typography>
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                className='primary-button'
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress
                    size={24} // Size of the spinner
                    sx={{
                      color: '#000',
                    }}
                  />
                ) : (
                  '提交回执' // Button label when not loading
                )}
              </Button>
            </Box>
            : <Box gap="16px" sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', margin: '8px 0 0', minWidth: 0 }}>
              <Button
                variant="outlined"
                className='outline-button'
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
                onClick={() => setIsCaptchaOpen(true)}
              >
                撤销转账
              </Button>
              <Button
                variant="contained"
                className='primary-button'
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
                onClick={handleConfirmTransfer}
              >
                我已转账
              </Button>
            </Box>
        }
      </Box>
      <CaptchaDialog
        open={isCaptchaOpen}
        onClose={() => setIsCaptchaOpen(false)}
        onSuccess={() => handleCancel()}
        title='撤销转账'
        message='请输入验证码以完成撤销'
      />
    </Box>
  );
};

export default PayoutForm;